import { FetchError } from "@medusajs/js-sdk";
import type { MutationOptions } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { sdk } from "@/lib/client";

const SEED_STATUS_POLL_MS = 2000;
const SEED_STATUS_MAX_POLL_MS = 10 * 60 * 1000;

export type AdminSeedStartResponse = Record<string, unknown>;

export type AdminSeedStatusResponse = Record<string, unknown>;

export type SeedWorkflowState = "idle" | "running" | "completed" | "failed" | "unknown";

function readSeedPayload(root: Record<string, unknown>): Record<string, unknown> {
  const nested = root.seed;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  return root;
}

export function deriveSeedWorkflowState(data: unknown): SeedWorkflowState {
  if (data === null || data === undefined) {
    return "unknown";
  }
  if (typeof data !== "object" || Array.isArray(data)) {
    return "unknown";
  }
  const root = data as Record<string, unknown>;
  const o = readSeedPayload(root);
  const statusRaw = o.status;
  const stateRaw = o.state;
  const status = typeof statusRaw === "string" ? statusRaw.toLowerCase() : "";
  const stateStr = typeof stateRaw === "string" ? stateRaw.toLowerCase() : "";
  const combined = status || stateStr;
  if (combined === "completed" || combined === "complete" || combined === "success") {
    return "completed";
  }
  if (o.completed === true) {
    return "completed";
  }
  if (combined === "failed" || combined === "error") {
    return "failed";
  }
  if (o.failed === true) {
    return "failed";
  }
  if (
    combined === "running" ||
    combined === "in_progress" ||
    combined === "pending" ||
    combined === "started"
  ) {
    return "running";
  }
  if (o.running === true || o.in_progress === true) {
    return "running";
  }
  if (
    combined === "idle" ||
    combined === "not_started" ||
    combined === "stopped"
  ) {
    return "idle";
  }
  return "unknown";
}

export const adminSeedStatusQueryKey = ["admin", "seed", "status"] as const;

export function useAdminSeedStatus(
  enabled: boolean,
  activelyAwaitingOutcome: boolean = false,
) {
  const [pollTimedOut, setPollTimedOut] = useState(false);

  const query = useQuery<AdminSeedStatusResponse, FetchError>({
    queryKey: adminSeedStatusQueryKey,
    queryFn: () =>
      sdk.client.fetch<AdminSeedStatusResponse>("/admin/seed/status", {
        method: "GET",
      }),
    enabled,
    staleTime: 0,
    refetchInterval: (q) => {
      if (pollTimedOut) {
        return false;
      }
      const state = deriveSeedWorkflowState(q.state.data);
      if (state === "running") {
        return SEED_STATUS_POLL_MS;
      }
      if (
        activelyAwaitingOutcome &&
        (state === "unknown" || state === "idle")
      ) {
        return SEED_STATUS_POLL_MS;
      }
      return false;
    },
  });

  const derivedState = deriveSeedWorkflowState(query.data);

  useEffect(() => {
    if (!enabled) {
      setPollTimedOut(false);
      return;
    }
    const needsWatchdog =
      derivedState === "running" ||
      (activelyAwaitingOutcome &&
        (derivedState === "unknown" || derivedState === "idle"));
    if (!needsWatchdog) {
      setPollTimedOut(false);
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setPollTimedOut(true);
    }, SEED_STATUS_MAX_POLL_MS);
    return () => window.clearTimeout(timeoutId);
  }, [derivedState, enabled, activelyAwaitingOutcome]);

  return {
    ...query,
    workflowState: derivedState,
    pollTimedOut,
    resetPollTimeout: () => setPollTimedOut(false),
  };
}

export function useAdminStartSeed(
  options?: MutationOptions<AdminSeedStartResponse, FetchError, void>,
) {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options ?? {};
  return useMutation({
    mutationFn: async () =>
      sdk.client.fetch<AdminSeedStartResponse>("/admin/seed/start", {
        method: "POST",
      }),
    ...restOptions,
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({ queryKey: adminSeedStatusQueryKey });
      userOnSuccess?.(data, variables, context);
    },
  });
}
